'use client';

import { useLocale } from 'next-intl';
import { useRouter, usePathname } from 'next/navigation';
import Image from 'next/image';
import { Globe } from 'lucide-react';
import { locales, localeNames, type Locale } from '@/i18n/config';

interface LanguageSwitcherProps {
  scrolled?: boolean;
  variant?: 'desktop' | 'mobile';
}

export default function LanguageSwitcher({ scrolled = false, variant = 'desktop' }: LanguageSwitcherProps) {
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();

  const switchLocale = (newLocale: Locale) => {
    // Remove current locale prefix and add new one
    const segments = pathname.split('/');
    segments[1] = newLocale;
    const newPath = segments.join('/');
    router.push(newPath);
  };

  if (variant === 'mobile') {
    return (
      <div className="flex items-center justify-center gap-2">
        {locales.map((loc) => (
          <button
            key={loc}
            onClick={() => switchLocale(loc)}
            className={`px-5 py-3 rounded-xl font-medium transition-all cursor-pointer ${
              locale === loc ? 'bg-cerad-green text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <Image
              src={`/images/flags/${loc}.svg`}
              alt={localeNames[loc as Locale]}
              width={20}
              height={12}
              className="inline-block mr-2 object-contain"
            />
            {loc.toUpperCase()}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="relative group">
      <button
        className={`flex items-center gap-1.5 px-4 py-3 rounded-full font-medium transition-all cursor-pointer ${
          scrolled
            ? 'text-gray-700 hover:text-cerad-green hover:bg-cerad-green/5'
            : 'text-white/90 hover:text-white hover:bg-white/10'
        }`}
      >
        <Globe className="w-4 h-4" />
        <span className="uppercase text-sm">{locale}</span>
      </button>

      <div className="absolute top-full right-0 mt-2 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 min-w-[120px]">
        {locales.map((loc) => (
          <button
            key={loc}
            onClick={() => switchLocale(loc)}
            aria-current={locale === loc ? 'true' : undefined}
            className={`w-full pl-2 pr-5 py-2 text-left hover:bg-gray-100 transition-colors duration-150 flex items-center gap-1 cursor-pointer ${
              locale === loc
                ? 'bg-cerad-green/5 text-cerad-green border-l-4 border-cerad-green'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Image
              src={`/images/flags/${loc}.svg`}
              alt={localeNames[loc as Locale]}
              width={24}
              height={16}
              className="inline-block object-contain"
            />
            <div>
              <span className="font-medium">{localeNames[loc as Locale]}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
